import { useRef, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import { Icon } from 'components/icon';
import { useAppSelector } from 'store';
import { Image } from 'components/image';
import { Button } from 'components/button';
import { Skeleton } from 'components/skeleton';
import { isDesktop, useClickOutside } from 'hooks';
import { NotDesktop } from 'components/responsive';
import { ChoiceTheme } from 'components/choice-theme';
import { selectAuthUserRole } from 'store/slices/auth';
import { useMyCompanyInfoQuery } from 'store/api/company';
import { ChoiceUserStatus } from 'components/choice-user-status';
import {
  Roles,
  BadgeColor,
  BadgePadding,
  ButtonColors,
  ButtonStyleTypes,
} from 'types';

import * as Styles from './styles';
import { LinkProps, SideBarProps } from './types';

const visibleClass = 'side-bar-visible';

const SidebarLink = ({
  path,
  icon,
  title,
  count,
  onClick,
  activeOn,
  disabled,
  lastItemAtBottom = false,
  ...rest
}: LinkProps & Pick<SideBarProps, 'lastItemAtBottom'>) => {
  const { pathname } = useLocation();
  const lit = Boolean(
    activeOn?.some(
      (root) => pathname === root || pathname.startsWith(`${root}/`)
    )
  );
  const content = (
    <Styles.ContextBlock>
      {icon}
      {!!count && (
        <Styles.Count
          smallFont
          text={String(count)}
          padding={BadgePadding.Small}
          styleType={BadgeColor.RedAlert}
        />
      )}
      <Styles.TitleLink>{title}</Styles.TitleLink>
    </Styles.ContextBlock>
  );

  return (
    <Styles.SideBarLink
      onClick={onClick}
      disabled={disabled ?? false}
      lastItemAtBottom={lastItemAtBottom}
      {...rest}
    >
      {path ? (
        <NavLink
          to={path}
          className={({ isActive }) => (isActive || lit ? 'active' : '')}
        >
          {content}
        </NavLink>
      ) : (
        content
      )}
    </Styles.SideBarLink>
  );
};

const SideBar = ({
  links,
  options,
  isWidget,
  isOpened,
  className,
  setOpened,
  onSignOut,
  userInfoProps,
  lastItemAtBottom = false,
  closeOnDocumentClick = true,
}: SideBarProps) => {
  const refMenu = useRef(null);
  const authUserRole = useAppSelector(selectAuthUserRole);

  const desktop = isDesktop();
  const close = () => setOpened(false);

  const { isLoading, data: company } = useMyCompanyInfoQuery(undefined, {
    skip: authUserRole === Roles.SITE_ADMIN,
  });

  const isVisible = isOpened || desktop;

  useEffect(() => {
    if (isVisible) {
      document.body.classList.add(visibleClass);
    } else {
      document.body.classList.remove(visibleClass);
    }
  }, [isVisible]);

  useEffect(() => {
    return () => document.body.classList.remove(visibleClass);
  }, []);

  useClickOutside(refMenu, (e) => {
    if (
      isOpened &&
      closeOnDocumentClick &&
      (e.target as HTMLDivElement)?.id !== 'burger'
    ) {
      close();
    }
  });

  const companyLogo = company?.photoUrl ? (
    <Image alt="Logo" width={48} height={48} src={company?.photoUrl} />
  ) : (
    <Icon.Logo />
  );

  return (
    <div ref={refMenu} className={className}>
      <Styles.SideBarBlock isOpen={isOpened} isWidget={isWidget}>
        <Styles.LogoItem to="/">
          {isLoading ? (
            <Skeleton width={48} height={48} borderRadius="16px" />
          ) : (
            companyLogo
          )}
        </Styles.LogoItem>

        <NotDesktop>
          <Styles.UserInfoWrap {...userInfoProps}>
            <Styles.ButtonWrap>
              <Button
                fullWidth
                text="Sign out"
                colorType={ButtonColors.Red}
                styleType={ButtonStyleTypes.Ghost}
                onClick={() => {
                  close();
                  onSignOut();
                }}
              />
            </Styles.ButtonWrap>
          </Styles.UserInfoWrap>
        </NotDesktop>

        {links.map(
          ({ icon, path, title, count, onClick, disabled, ...rest }) => (
            <Styles.Popup
              on="hover"
              key={path ?? title}
              closeOnDocumentClick
              position="right center"
              trigger={
                <div>
                  <SidebarLink
                    icon={icon}
                    path={path}
                    title={title}
                    count={count}
                    onClick={onClick}
                    disabled={disabled}
                    lastItemAtBottom={lastItemAtBottom}
                  />
                </div>
              }
              {...rest}
            >
              <Styles.Tooltip>{title}</Styles.Tooltip>
            </Styles.Popup>
          )
        )}

        <NotDesktop>
          <Styles.ThemeBlock>
            <ChoiceUserStatus options={options} />

            <ChoiceTheme padding="16px 16px 0" />
          </Styles.ThemeBlock>
        </NotDesktop>
      </Styles.SideBarBlock>

      <Styles.OpenButton isOpen={isOpened} onClick={() => setOpened(!isOpened)}>
        <Icon.ChevronRight />
      </Styles.OpenButton>
    </div>
  );
};

export { SideBar };
export type { LinkProps };
